import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Copy } from "lucide-react";

export default function ForceTwoFactorSetup() {
  const { setUser, logout } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);

  useEffect(() => {
    api.post("/auth/2fa/setup").then(({ data }) => setSetup(data))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not start 2FA setup"));
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      setCodes(data.backup_codes || []);
      toast.success("Two-factor authentication enabled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "That code didn't match. Try a fresh one.");
    } finally { setBusy(false); }
  };

  const finish = async () => {
    const me = await api.get("/auth/me");
    setUser(me.data);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-10" data-testid="force-2fa-setup">
      <div className="w-full max-w-md bg-card border border-border rounded-lg p-8 shadow-sm">
        <div className="flex items-center gap-2 text-accent"><ShieldCheck className="w-6 h-6" /><span className="font-display text-xl font-bold">Two-factor authentication required</span></div>
        <p className="text-sm text-muted-foreground mt-2">Your role requires 2FA. Scan the QR code with an authenticator app (Google Authenticator, Authy, 1Password), then enter a 6-digit code to continue.</p>

        {!codes && setup && (
          <div className="mt-6 space-y-4">
            <div className="flex justify-center"><img src={setup.qr_code} alt="2FA QR code" className="w-44 h-44 border border-border rounded-md" data-testid="force-2fa-qr" /></div>
            <div className="text-xs text-muted-foreground break-all bg-muted rounded p-2">Manual key: <span className="font-mono">{setup.secret}</span></div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">6-digit code</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" placeholder="123456" data-testid="force-2fa-code" />
            </div>
            <Button onClick={enable} disabled={busy || code.length < 6} className="w-full h-11" data-testid="force-2fa-enable">{busy ? "Verifying…" : "Enable & Continue"}</Button>
          </div>
        )}

        {codes && (
          <div className="mt-6 space-y-4" data-testid="force-2fa-backup">
            <p className="text-sm font-medium">Save your backup codes</p>
            <p className="text-xs text-muted-foreground">Store these somewhere safe — each can be used once if you lose your device.</p>
            <div className="grid grid-cols-2 gap-2 bg-muted rounded p-3 font-mono text-xs">
              {codes.map((c) => <div key={c}>{c}</div>)}
            </div>
            <Button variant="outline" className="w-full" onClick={() => { navigator.clipboard.writeText(codes.join("\n")); toast.success("Copied"); }}><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy codes</Button>
            <Button onClick={finish} className="w-full h-11" data-testid="force-2fa-done">I've saved them — Continue</Button>
          </div>
        )}

        <button onClick={logout} className="mt-4 text-xs text-muted-foreground hover:text-foreground underline w-full text-center" data-testid="force-2fa-logout">Sign out instead</button>
      </div>
    </div>
  );
}
